import React, { useCallback } from 'react';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import useUser from '@vl-core/hooks/useUser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Modal, Result } from 'ALIASED-antd';
import useUserNextAppointments from '@hooks/useUserNextAppointments';
import { useCancelledAppointments } from '@hooks/useCancelledAppointments';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

const useReleaseAppointment = () => {
  const user = useUser();
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { appointment } = useSessionAppointment();
  const [, invalidateAppointments] = useUserNextAppointments(10);
  const { setCancelledAppointments } = useCancelledAppointments();

  const returnToAppointmentsPage = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['clinicianAppointment']
    });
    router.push('/clinician/appointments').then();
  }, [router, queryClient]);

  const { mutateAsync: releaseAppointment, isPending: isReleaseAppointmentLoading } = useMutation({
    mutationFn: async () => {
      const reasonResponse = await dispatch(
        actions.rescheduleCancel({
          user_guid: user.user_guid,
          appt_id: appointment.appointment_id.toString(),
          reason: 'APPT_NLR'
        })
      );
      if (reasonResponse.type === actionTypes.RESCHEDULE_CANCEL_SUCCESS) return { status: 200 };
      throw new Error('Failed to release case');
    },
    onSuccess: () => {
      // New Functionality
      Modal.success({
        centered: true,
        icon: <></>,
        width: '30rem',
        title: <Result status="info" title={t('Case Released')} subTitle="This case has now been released." />,
        modalRender: (originalNode) => <div data-testid="case-released-modal">{originalNode}</div>,
        onOk: () => {
          invalidateAppointments();
          setCancelledAppointments((a) => ({ ...a, [appointment.appointment_id]: true }));
          returnToAppointmentsPage();
        },
        okText: 'Go to Appointments',
        okButtonProps: { style: { display: 'block', margin: '0 auto 2rem auto' } }
      });
    },
    onError: () => {
      Modal.error({
        title: t('Release Case'),
        modalRender: (originalNode) => <div data-testid="release-case-modal">{originalNode}</div>,
        content: 'Failed to release the case'
      });
    }
  });

  const onReleaseAppointment = useCallback(() => {
    if (appointment.appointment_completed) {
      Modal.error({
        title: t('Unable to release appointment'),
        modalRender: (originalNode) => <div data-testid="unable-to-release-appointment-modal">{originalNode}</div>,
        content: 'This case review has already been completed and cannot be cancelled.',
        okText: 'Go to Appointments',
        closable: true,
        centered: true,
        onOk: () => {
          returnToAppointmentsPage();
        }
      });
      return;
    }
    Modal.confirm({
      title: t('Release Case?'),
      modalRender: (originalNode) => <div data-testid="release-case-modal">{originalNode}</div>,
      centered: true,
      style: { minWidth: '20vw' },
      okText: 'Confirm',
      content: 'Releasing the case will delete any clinician-added data for the case',
      okButtonProps: { loading: isReleaseAppointmentLoading },
      onOk: () => {
        Modal.destroyAll();
        releaseAppointment();
      }
    });
  }, [appointment, isReleaseAppointmentLoading, releaseAppointment, returnToAppointmentsPage]);

  return {
    onReleaseAppointment
  };
};

export { useReleaseAppointment };
